"use client";

/**
 * Host shell for the Office add-in surfaces (Excel, Word, Outlook) — the
 * `shell` facet of the Office embedded-host descriptor, registered by surface
 * in `components/embedded-hosts.tsx`. Mounted by `HostProvider` when the
 * current surface is an Office add-in.
 *
 * It:
 *   - Injects a synchronous inline script that locks down
 *     `History.pushState` / `replaceState` BEFORE Office.js loads, so Next.js
 *     App Router navigation keeps working inside the WebView.
 *   - Loads `office.js` from Microsoft's CDN.
 *   - Mounts {@link OfficeProvider} (publishes Office.onReady state) and
 *     {@link NestedMsalAuthProvider} (silent MSAL NAA + Better-Auth exchange).
 *   - Forces the theme to light — the Office ribbon is always light and dark
 *     mode looks jarring inside the constrained task pane.
 *   - Gates `children` on a session check, showing a loading or sign-in screen
 *     until MSAL has produced a Better-Auth session.
 */
import { c as _c } from "react/compiler-runtime";
import Script from "next/script";
import { useTheme } from "next-themes";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { EmbeddedGateScreen } from "@/components/embedded/gate-screen";
import { HISTORY_RESTORE_SCRIPT } from "@/lib/excel/office-compat";
import { useHost } from "@/lib/host-context";
import { getOfficeAddinSessionHeaders } from "@/lib/office-addin-session";
import { startWordSelectionTracking } from "@/lib/word/selection-tracker";
import { NestedMsalAuthProvider, useNestedMsalAuth } from "./nested-msal-auth-provider";
import { OfficeProvider, useOffice } from "./office-provider";
export function OfficeShell(t0) {
  const $ = _c(10);
  const {
    children
  } = t0;
  const host = useHost();
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <script dangerouslySetInnerHTML={{
      __html: HISTORY_RESTORE_SCRIPT
    }} />;
    t2 = <Script crossOrigin="anonymous" src="https://appsforoffice.microsoft.com/lib/1/hosted/office.js" />;
    t3 = <LightThemeLock />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== host) {
    t4 = host === "word-add-in" && <WordSelectionTrackerInit />;
    $[3] = host;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== children) {
    t5 = <NestedMsalAuthProvider><AddinAuthGate>{children}</AddinAuthGate></NestedMsalAuthProvider>;
    $[5] = children;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <>{t1}{t2}{t3}<OfficeProvider>{t4}{t5}</OfficeProvider></>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}

/**
 * Starts the Word selection tracker once Office is ready. Mounted only on the
 * word-add-in surface. See `lib/word/selection-tracker.ts` for why we need a
 * bookmark-backed cache of the user's last selection.
 */
function WordSelectionTrackerInit() {
  const $ = _c(3);
  const {
    isReady
  } = useOffice();
  let t0;
  let t1;
  if ($[0] !== isReady) {
    t0 = () => {
      if (!isReady) {
        return;
      }
      startWordSelectionTracking().catch(_temp);
    };
    t1 = [isReady];
    $[0] = isReady;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return null;
}

/**
 * Forces the next-themes theme to "light" on every mount. Office hosts are
 * always light-themed and the task pane is too cramped for dark mode to look
 * right.
 */
function _temp(err) {
  console.warn("[Magnus Word] Could not start selection tracker", err);
}
function LightThemeLock() {
  const $ = _c(3);
  const {
    setTheme
  } = useTheme();
  let t0;
  let t1;
  if ($[0] !== setTheme) {
    t0 = () => {
      setTheme("light");
    };
    t1 = [setTheme];
    $[0] = setTheme;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return null;
}

/**
 * Sits between MSAL and the rest of the app:
 *
 * 1. Waits for Office.js to be ready ("Connecting to Office...").
 * 2. Pings `/api/auth/office-addin-verify` (with the Bearer header) to see if
 *    we already have a Better-Auth session, re-checking each time MSAL flips
 *    `isAuthenticated`.
 * 3. While neither MSAL has finished nor a session exists, renders a loading
 *    screen, then a sign-in screen if MSAL ultimately failed.
 * 4. Once a Bearer-resolvable session exists, renders `children`.
 *
 * NOTE: We deliberately do NOT call `router.refresh()` after auth. WebView2
 * sometimes drops the `Set-Cookie` from the office-addin exchange
 * (Edge+localhost+self-signed cert combinations vary), so an RSC refresh would
 * keep returning `null` from server pages and we'd hang on a "Loading
 * Magnus..." screen. Instead, the protected pages render their client shells
 * with safe defaults when the server-side session is missing in an add-in
 * context — every client query carries the Bearer header, so data loads as
 * soon as MSAL completes regardless of cookie behaviour.
 */
function AddinAuthGate({
  children
}: {
  children: ReactNode;
}) {
  const {
    isReady: officeReady,
    host
  } = useOffice();
  const {
    isInitializing,
    isAuthenticated,
    error: authError,
    login
  } = useNestedMsalAuth();
  const [sessionCheck, setSessionCheck] = useState<"checking" | "has-session" | "no-session">("checking");
  const checkSession = useCallback(async () => {
    try {
      // Use the layered `getSession()` endpoint (Bearer → embedded cookie →
      // Better Auth cookie) rather than Better Auth's `/api/auth/get-session`,
      // which only reads its own cookie and silently returns null for any
      // Bearer-only addin session.
      const res = await fetch("/api/auth/office-addin-verify", {
        credentials: "include",
        headers: {
          ...getOfficeAddinSessionHeaders()
        }
      });
      if (!res.ok) {
        return "no-session" as const;
      }
      const data = await res.json();
      return data?.session ? "has-session" as const : "no-session" as const;
    } catch {
      return "no-session" as const;
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-check session after MSAL auth changes isAuthenticated
  useEffect(() => {
    let cancelled = false;
    checkSession().then(result => {
      if (!cancelled) {
        setSessionCheck(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [checkSession, isAuthenticated]);
  const handleSignIn = useCallback(async () => {
    await login();
  }, [login]);
  if (!officeReady || sessionCheck === "checking") {
    return <EmbeddedGateScreen message="Connecting to Office..." spinner />;
  }
  if (isInitializing && sessionCheck === "no-session") {
    return <EmbeddedGateScreen message="Authenticating..." spinner />;
  }
  if (sessionCheck === "no-session" && !isAuthenticated) {
    return <EmbeddedGateScreen action={{
      label: "Sign in with Microsoft",
      onClick: handleSignIn
    }} error={authError} icon={<SignInIcon />} message={`Sign in with your Microsoft account to use the AI assistant${host ? ` in ${host}` : ""}.`} title="Sign in to Magnus" />;
  }
  return <>{children}</>;
}

/** Decorative avatar glyph for the Office sign-in gate. */
function SignInIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex size-16 items-center justify-center rounded-2xl bg-primary/10"><svg aria-hidden="true" className="size-8 text-primary" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" strokeLinecap="round" strokeLinejoin="round" /></svg></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}